import { SlideFrame } from '../SlideFrame';

// Two-jobs framing (Steve 2026-06-03). The live presentation frames the
// Visualizer as doing TWO things — find the best companies, then watch them
// and act. This slide now leads with those two jobs as the spine; the six
// capabilities nest underneath (3 each) instead of reading as a flat feature
// grid. `universe` is threaded from page.tsx so the count stays live.
function buildJobs(universe: number) {
  return [
    {
      num: '1',
      job: 'Find the best companies',
      tagline: 'Rank the whole market every morning. Narrow it to the ten that matter.',
      capabilities: [
        {
          title: 'Universe Expansion',
          hero: `${universe.toLocaleString()} companies`,
          description: 'US, Canada, and global ADRs — wider than any single team could cover with rigour.',
        },
        {
          title: 'Alpha Zone Selection',
          hero: 'Top 10 by composite',
          description: 'Quality conditional on cheap valuation, scored across 54 factors and six peer groups.',
        },
        {
          title: 'Research Analyst',
          hero: 'Four lenses',
          description: 'Ideas, movers, leaderboard, history lab — the high-conviction call, surfaced by morning.',
        },
      ],
    },
    {
      num: '2',
      job: 'Watch them. Act.',
      tagline: 'Monitor every holding around the clock. Manage the risk when it is time.',
      capabilities: [
        {
          title: 'Pipeline',
          hero: '620,000 datapoints / day',
          description: 'Bloomberg + FMP refreshed every weekday before seven. Detectors flag events in real time.',
        },
        {
          title: 'Portfolio Tracking',
          hero: '4 daily feeds',
          description: 'SEC filings, Congressional trades, insider Form 4s, earnings-call transcripts — per holding.',
        },
        {
          title: 'Hedge Lab',
          hero: '4 strategies',
          description: 'Trim, covered call, collar, replacement — ranked against live option chains, Canadian tax in.',
        },
      ],
    },
  ];
}

export function Slide04FourTools({ universe }: { universe: number }) {
  const jobs = buildJobs(universe);
  return (
    <SlideFrame pageNum={7} title="THE VISUALIZER  —  WHAT IT DOES"
      banner="Two jobs: find the best companies, then watch them like a hawk.">
      <div className="h-full grid grid-cols-2 gap-6 px-8 py-2">
        {jobs.map(j => (
          <div key={j.num} className="flex flex-col gap-2.5">
            {/* Job header — the spine. Big gold number + the job in one line. */}
            <div className="flex items-baseline gap-3 border-b border-[#c9a84c]/40 pb-2">
              <span className="text-[#c9a84c] text-[42px] font-bold leading-none tabular-nums">{j.num}</span>
              <div>
                <div className="text-[#c9a84c] text-[24px] font-bold leading-tight">{j.job}</div>
                <div className="text-[#94a3b8] text-[13px] leading-snug mt-0.5">{j.tagline}</div>
              </div>
            </div>
            {/* Nested capabilities — how the job gets done. */}
            {j.capabilities.map(c => (
              <div key={c.title} className="bg-[#1e293b] rounded-xl border border-[#c9a84c]/20 px-4 py-2.5 flex flex-col flex-1 justify-center">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="text-white text-[16px] font-semibold leading-tight">{c.title}</span>
                  <span className="text-[#c9a84c] text-[14px] font-bold tabular-nums whitespace-nowrap">{c.hero}</span>
                </div>
                <div className="text-[#cbd5e1] text-[13px] leading-snug">{c.description}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </SlideFrame>
  );
}
